import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import CartDrawer from './CartDrawer';
import SearchModal from './SearchModal';

const navLinks = [
  { to: '/', label: 'HOME' },
  { to: '/shop', label: 'SHOP' },
  { to: '/supplies', label: 'SUPPLIES' },
  { to: '/about', label: 'ABOUT' },
  { to: '/faq', label: 'FAQ' },
  { to: 'mailto:vialratpayments@gmail.com', label: 'CONTACT', external: true },
];

const Header = () => {
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0a0a0b]/85 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-white/10">
              <img src="https://static.prod-images.emergentagent.com/jobs/b6d9efba-b5a6-4827-a8fc-40656bb19f7f/images/af73646905a2fe69ee69541615836951a048af7f9144689781766cb4be1cbd27.jpeg" alt="Vial Rat" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-white text-[15px] tracking-wide">VIAL RAT.</div>
              <div className="font-mono-label text-[9px] text-neutral-500">RESEARCH CO.</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navLinks.map(l => (
              l.external ? (
                <a
                  key={l.to}
                  href={l.to}
                  className="font-mono-label text-[11px] transition-colors text-neutral-300 hover:text-white"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === '/'}
                  className={({ isActive }) =>
                    `font-mono-label text-[11px] transition-colors ${isActive ? 'text-[#ff2fa1]' : 'text-neutral-300 hover:text-white'}`
                  }
                >
                  {l.label}
                </NavLink>
              )
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <button aria-label="Search" onClick={() => setSearchOpen(true)} className="text-neutral-300 hover:text-[#ff2fa1] transition-colors">
              <Search size={20} />
            </button>
            <Link to="/wishlist" aria-label="Wishlist" className="relative text-neutral-300 hover:text-[#ff2fa1] transition-colors">
              <Heart size={20} />
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#ff2fa1] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{wishCount}</span>
              )}
            </Link>
            <button aria-label="Cart" onClick={() => setOpen(true)} className="relative text-neutral-300 hover:text-[#ff2fa1] transition-colors">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#ff2fa1] text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>
              )}
            </button>
            <button aria-label="Menu" onClick={() => setMobileOpen(true)} className="md:hidden text-neutral-300">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#0a0a0b] md:hidden">
          <div className="flex justify-end p-6">
            <button aria-label="Close" onClick={() => setMobileOpen(false)}><X size={24} /></button>
          </div>
          <div className="flex flex-col items-center gap-8 mt-10">
            {navLinks.map(l => (
              l.external ? (
                <a key={l.to} href={l.to} onClick={() => setMobileOpen(false)}
                  className="font-mono-label text-sm text-neutral-200">
                  {l.label}
                </a>
              ) : (
                <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `font-mono-label text-sm ${isActive ? 'text-[#ff2fa1]' : 'text-neutral-200'}`}>
                  {l.label}
                </NavLink>
              )
            ))}
          </div>
        </div>
      )}

      <CartDrawer />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Header;
